import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, BookOpen, Search, Crown, Play, Lock } from 'lucide-react';

function SeriesCard({ series, onClick }) {
  return (
    <Card 
      className="bg-surface border-border cursor-pointer group overflow-hidden transition-all hover:border-ruby/50 hover:shadow-lg hover:shadow-ruby/10"
      onClick={onClick}
      data-testid={`series-card-${series.id}`}
    >
      {/* Cover Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        {series.cover_image ? (
          <img 
            src={series.cover_image} 
            alt={series.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-ruby/30 to-ruby/10 flex items-center justify-center">
            <BookOpen className="w-16 h-16 text-ruby/50" />
          </div>
        )}
        {/* Premium Badge */}
        <div className="absolute top-3 right-3">
          <Badge className="bg-ruby text-white font-bold px-3 py-1">
            <Crown className="w-3 h-3 mr-1" />
            Premium
          </Badge>
        </div>
        {/* Episode Count */}
        <div className="absolute bottom-3 left-3">
          <Badge variant="secondary" className="bg-black/70 text-white">
            <Play className="w-3 h-3 mr-1" />
            {series.episode_count} episódios
          </Badge>
        </div>
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-semibold text-foreground text-lg mb-2 group-hover:text-ruby transition-colors line-clamp-1">
          {series.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {series.description}
        </p>
      </CardContent>
    </Card>
  );
}

export default function Series({
  seriesType = 'story',
  pageTitle = 'Séries de Histórias',
  pageSubtitle = 'Mergulhe em histórias épicas contadas em episódios. Compra uma vez, lê para sempre.',
  badgeLabel = 'Séries Exclusivas',
  detailRoutePrefix = '/series',
  searchPlaceholder = 'Pesquisar séries…',
  emptyTitle = 'Ainda não há séries disponíveis',
  emptyTitleSearching = 'Nenhuma série encontrada',
}) {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesType]);

  const loadSeries = async () => {
    setLoading(true);
    try {
      const res = await api.get('/series', { params: { series_type: seriesType } });
      setSeries(res.data);
    } catch (error) {
      console.error('Error loading series:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSeries = searchTerm
    ? series.filter(s => 
        s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : series;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Hero Section */}
        <div className="relative py-16 px-6 mb-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-ruby/20 via-transparent to-ruby/10" />
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-ruby/20 px-4 py-2 rounded-full mb-6">
              <BookOpen className="w-4 h-4 text-ruby" />
              <span className="text-ruby font-medium">{badgeLabel}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {pageTitle}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {pageSubtitle}
            </p>
            
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-surface border-border"
                data-testid="series-search"
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-ruby" />
            </div>
          ) : filteredSeries.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSeries.map((s) => (
                <SeriesCard 
                  key={s.id} 
                  series={s}
                  onClick={() => navigate(`${detailRoutePrefix}/${detailRoutePrefix === '/series' && s.slug ? s.slug : s.id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-muted mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {searchTerm ? emptyTitleSearching : emptyTitle}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Tenta outros termos de pesquisa' : 'Volta em breve para novidades!'}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
